import { useEffect, useState } from "react";
import { Upload, Download, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useSeo } from "../lib/seo";

export default function CsvImport() {
  useSeo({ title: "CSV Import — Admin", robots: "noindex,nofollow" });
  const [template, setTemplate] = useState(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/admin/import/template").then((r) => setTemplate(r.data)); }, []);

  const downloadTemplate = () => {
    if (!template) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = template.columns.join(",") + "\n" + template.columns.map((c) => esc(template.example[c])).join(",") + "\n";
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "articleblogs-import-template.csv"; a.click();
  };

  const validate = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true); setResult(null); setDone(null);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/import/validate", fd); setResult(data); } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/import/commit", { rows: result.valid.map((r) => r.data) });
      setDone(data); toast.success(`Imported ${data.count} article${data.count === 1 ? "" : "s"}`);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-csv-import">
      <p className="eyebrow">Bulk content</p>
      <h1 className="mt-1 font-serif text-3xl font-semibold">CSV Import</h1>
      <p className="mt-2 max-w-2xl text-sm text-ab-muted">Upload a UTF-8 CSV, validate every row, preview, then import only the valid rows. Columns: <code className="rounded bg-ab-soft px-1 font-mono text-xs">{template?.columns.join(", ")}</code></p>

      <div className="mt-6 grid gap-6 lg:grid-cols-12">
        <form onSubmit={validate} className="card-ab space-y-4 p-6 lg:col-span-4" data-testid="csv-upload-form">
          <label className="label-ab" htmlFor="csv-file">CSV file</label>
          <input id="csv-file" type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-ab-accent-soft file:px-4 file:py-2 file:text-sm file:font-semibold file:text-ab-accent-hover" data-testid="csv-file-input" />
          <button type="submit" disabled={!file || busy} className="btn-primary w-full disabled:opacity-50" data-testid="csv-validate-button"><Upload size={15} /> {busy ? "Validating…" : "Upload & validate"}</button>
          <button type="button" onClick={downloadTemplate} className="btn-secondary w-full" data-testid="csv-template-button"><Download size={15} /> Download template</button>
        </form>

        <div className="space-y-6 lg:col-span-8">
          {result && !done && (
            <>
              <div className="grid grid-cols-3 gap-3">
                {[["Rows", result.total], ["Valid", result.valid.length], ["Errors", result.errors.length]].map(([l, v]) => <div key={l} className="card-ab p-4" data-testid={`csv-stat-${l.toLowerCase()}`}><p className="text-xs uppercase tracking-wider text-ab-muted">{l}</p><p className="mt-1 font-serif text-2xl font-semibold">{v}</p></div>)}
              </div>
              {result.errors.length > 0 && (
                <div className="card-ab border-amber-200 bg-amber-50 p-5" data-testid="csv-errors">
                  <h2 className="flex items-center gap-2 font-semibold text-amber-900"><AlertTriangle size={16} /> Rows with errors (will be skipped)</h2>
                  <ul className="mt-3 space-y-2 text-sm text-amber-900">{result.errors.map((e) => <li key={e.row}><strong>Row {e.row}</strong> {e.title && `· ${e.title}`}<ul className="ml-4 list-disc text-xs">{e.errors.map((x, i) => <li key={i}>{x}</li>)}</ul></li>)}</ul>
                </div>
              )}
              <div className="card-ab overflow-x-auto" data-testid="csv-preview">
                <table className="w-full text-sm"><thead className="bg-ab-soft text-left text-xs uppercase tracking-wider text-ab-muted"><tr><th className="px-4 py-2">Row</th><th className="px-4 py-2">Title</th><th className="px-4 py-2">Category</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Notes</th></tr></thead>
                  <tbody className="divide-y divide-ab-border">{result.valid.map((r) => <tr key={r.row}><td className="px-4 py-2 text-ab-muted">{r.row}</td><td className="px-4 py-2 font-medium">{r.data.title}</td><td className="px-4 py-2">{r.data.category}</td><td className="px-4 py-2 capitalize">{r.data.status}</td><td className="px-4 py-2 text-xs text-amber-700">{r.warnings.join("; ")}</td></tr>)}</tbody>
                </table>
              </div>
              <button type="button" onClick={commit} disabled={busy || result.valid.length === 0} className="btn-primary disabled:opacity-50" data-testid="csv-import-button"><CheckCircle2 size={15} /> Import {result.valid.length} valid row{result.valid.length === 1 ? "" : "s"}</button>
            </>
          )}
          {done && (
            <div className="card-ab bg-ab-accent-soft p-6" data-testid="csv-import-done">
              <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold text-[#1D5239]"><CheckCircle2 /> Imported {done.count} article{done.count === 1 ? "" : "s"}</h2>
              <ul className="mt-3 space-y-1 text-sm">{done.created.map((c) => <li key={c.id}>{c.title} <span className="text-ab-muted">· /{c.slug}</span></li>)}</ul>
              {done.failed.length > 0 && <p className="mt-3 text-sm text-red-700">{done.failed.length} failed: {done.failed.map((f) => f.title).join(", ")}</p>}
            </div>
          )}
          {!result && !done && <div className="card-ab bg-ab-soft p-10 text-center text-sm text-ab-muted">Validation results and a preview table will appear here.</div>}
        </div>
      </div>
    </div>
  );
}
